import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
    ArrowLeft,
    Bookmark,
    BookmarkCheck,
    CalendarDays,
    MapPin,
    Sparkles,
    CheckCircle2,
    MessageCircle,
    Users,
    ShieldCheck,
    RotateCcw,
    Rocket,
    Flag,
} from "lucide-react";
import { getEventById, getEventAttendees, isEventPast } from "@/lib/entitiesStore";
import {
    isEventSaved,
    toggleSaveEvent,
    isEventJoined,
    joinEvent,
} from "@/lib/socialStore";
import {
    addPendingFunds,
    addTransaction,
    recordPaidTicket,
    getPaidTicket,
    isTicketRefunded,
    refundPaidTicket,
} from "@/lib/monetizationStore";
import { useAuth } from "@/context/AuthContext";
import { useSocialVersion, useMoneVersion } from "@/lib/hooks";
import InitialAvatar from "@/components/InitialAvatar";
import PaymentSheet from "@/components/PaymentSheet";
import ReportSheet from "@/components/ReportSheet";
import MoreMenu from "@/components/MoreMenu";

const EventDetail = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const { user } = useAuth();
    const v = useSocialVersion();
    const mv = useMoneVersion();
    const [slide, setSlide] = useState(0);
    const [showPay, setShowPay] = useState(false);
    const [justJoined, setJustJoined] = useState(false);
    const [refundDone, setRefundDone] = useState(false);
    const [reportOpen, setReportOpen] = useState(false);

    const event = useMemo(() => getEventById(id), [id]);
    const isHost = !!(user && event && event.ownerId === user.id);
    const saved = useMemo(
        () => (user && event ? isEventSaved(user.id, event.id) : false),
        [user, event, v]
    );
    // Hosts are automatically considered "joined"
    const joined = useMemo(
        () => (user && event ? (isHost || isEventJoined(user.id, event.id)) : false),
        [user, event, isHost, v]
    );
    const past = event ? isEventPast(event) : false;

    // Refund eligibility hooks — must run before any early return.
    const ticket = useMemo(
        () => (user && event ? getPaidTicket(user.id, event.id) : null),
        [user, event, mv]
    );
    const alreadyRefunded = useMemo(
        () => (user && event ? isTicketRefunded(user.id, event.id) : false),
        [user, event, mv]
    );

    if (!event) {
        return (
            <div className="screen items-center justify-center" data-testid="event-not-found">
                <p className="text-[14px] text-muted-foreground">Event not found.</p>
                <button onClick={() => navigate(-1)} className="mt-3 text-primary text-[13px] font-medium">
                    Go back
                </button>
            </div>
        );
    }

    const images = event.images?.length ? event.images : [event.image];
    const isFree = !event.price || event.price === 0;
    const priceLabel = isFree ? "Free" : `${event.currency}${Number(event.price).toLocaleString()}`;

    // Attendance — capacity-aware
    const { count, capacity } = getEventAttendees(event, joined);
    const isFull = !!(capacity && count >= capacity);
    const almostFull = !!(capacity && !isFull && count >= Math.floor(capacity * 0.8));
    const pct = capacity ? Math.min(100, Math.round((count / capacity) * 100)) : 0;
    const attendanceLabel = capacity
        ? `${count}/${capacity} attending`
        : `${count} attending`;

    const doJoin = () => {
        joinEvent(user.id, event.id);
        setJustJoined(true);
        setShowPay(false);
    };

    const handleJoin = () => {
        if (!user) return;
        if (joined || past || isFull) return;
        if (isFree) {
            doJoin();
        } else {
            setShowPay(true);
        }
    };

    const handlePaymentSuccess = () => {
        if (!user || !event) return;
        const amt = Number(event.price) || 0;
        // Park funds in host's pending wallet (escrow)
        if (event.ownerId) {
            addPendingFunds(event.ownerId, amt, {
                kind: "escrow_in",
                title: `Ticket sale — ${event.title}`,
                escrowId: `evt_${event.id}_${Date.now()}`,
            });
        }
        // Buyer transaction
        addTransaction(user.id, {
            type: "debit",
            kind: "event",
            title: `Ticket — ${event.title}`,
            amount: amt,
            eventId: event.id,
        });
        // Record ticket so refunds can be requested later
        recordPaidTicket(user.id, {
            eventId: event.id,
            title: event.title,
            amount: amt,
            sellerId: event.ownerId,
            refundPolicy: event.refundPolicy || "refundable",
        });
        doJoin();
    };

    // Refund eligibility: paid ticket, host cancelled, not yet refunded.
    const cancelled = !!event?.cancelled;
    const canRefund = !!ticket && cancelled && !alreadyRefunded && !isHost;

    const handleRequestRefund = () => {
        if (!user || !event) return;
        const r = refundPaidTicket(user.id, event.id, {
            title: `Refund — ${event.title}`,
        });
        if (r.ok) {
            setRefundDone(true);
            setTimeout(() => setRefundDone(false), 2600);
        }
    };

    const refundPolicyLabel =
        event?.refundPolicy === "non_refundable" ? "Non-refundable" : "Refundable";

    return (
        <div className="screen relative" data-testid="event-detail-screen">
            <div className="relative aspect-[16/11] bg-muted">
                <img
                    src={images[slide] || images[0]}
                    alt={event.title}
                    className="absolute inset-0 h-full w-full object-cover"
                />
                <div
                    aria-hidden
                    className="absolute inset-0"
                    style={{
                        background:
                            "linear-gradient(180deg, rgba(0,0,0,0.55), rgba(0,0,0,0) 40%, rgba(0,0,0,0) 65%, rgba(0,0,0,0.75))",
                    }}
                />
                <button
                    onClick={() => navigate(-1)}
                    data-testid="event-back-btn"
                    className="absolute top-[max(12px,env(safe-area-inset-top))] left-3 h-10 w-10 flex items-center justify-center rounded-full bg-background/85 backdrop-blur-md border border-border"
                    aria-label="Back"
                >
                    <ArrowLeft size={18} />
                </button>
                <button
                    onClick={() => user && toggleSaveEvent(user.id, event.id)}
                    data-testid="event-save-btn"
                    className="absolute top-[max(12px,env(safe-area-inset-top))] right-3 h-10 w-10 flex items-center justify-center rounded-full bg-background/85 backdrop-blur-md border border-border"
                    aria-label="Save"
                    aria-pressed={saved}
                >
                    {saved ? <BookmarkCheck size={18} className="text-primary" /> : <Bookmark size={18} />}
                </button>

                {!isHost && user && (
                    <div
                        className="absolute top-[max(12px,env(safe-area-inset-top))] right-14"
                        data-testid="event-more-menu-wrap"
                    >
                        <MoreMenu
                            target={{ kind: "event", id: event.id, name: event.title }}
                            testId="event-more"
                        />
                    </div>
                )}

                <span
                    data-testid="event-price-badge"
                    className="absolute top-[max(14px,env(safe-area-inset-top))] left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-[11.5px] font-semibold bg-background/90 backdrop-blur border border-border"
                >
                    {priceLabel}
                </span>

                <div className="absolute inset-x-0 bottom-0 p-5 text-white">
                    <span className="inline-block px-2.5 py-1 rounded-full text-[10.5px] font-semibold uppercase tracking-wider bg-primary/90 text-primary-foreground">
                        {event.category}
                    </span>
                    <h1 className="mt-2 font-display text-[22px] font-semibold tracking-tight leading-tight drop-shadow">
                        {event.title}
                    </h1>
                </div>

                {images.length > 1 && (
                    <div className="absolute bottom-20 right-4 flex gap-1.5">
                        {images.map((_, i) => (
                            <button
                                key={i}
                                data-testid={`event-carousel-dot-${i}`}
                                onClick={() => setSlide(i)}
                                aria-label={`Slide ${i + 1}`}
                                className={["h-1.5 rounded-full transition-all", i === slide ? "w-6 bg-white" : "w-1.5 bg-white/50"].join(" ")}
                            />
                        ))}
                    </div>
                )}
            </div>

            <div className="flex-1 overflow-y-auto no-scrollbar px-5 pt-4 pb-28">
                <ul className="space-y-2.5 text-[13.5px]">
                    <li className="flex items-start gap-2.5">
                        <CalendarDays size={16} className="text-primary mt-0.5 shrink-0" />
                        <span className="text-foreground/85">{event.date}</span>
                    </li>
                    <li className="flex items-start gap-2.5">
                        <MapPin size={16} className="text-primary mt-0.5 shrink-0" />
                        <span className="text-foreground/85">{event.location}</span>
                    </li>
                </ul>

                {event.description && (
                    <p className="mt-4 text-[13.5px] leading-relaxed text-foreground/85" data-testid="event-description">
                        {event.description}
                    </p>
                )}

                {/* Attendance */}
                <section
                    className="mt-5 p-4 rounded-2xl border border-border bg-card"
                    data-testid="event-attendance-section"
                >
                    <div className="flex items-center justify-between gap-3">
                        <span className="inline-flex items-center gap-2 text-[12.5px] text-foreground/85">
                            <Users size={14} className="text-primary" />
                            <span data-testid="event-attendance-label">
                                {joined && !isHost ? `You joined • ${attendanceLabel}` : attendanceLabel}
                            </span>
                        </span>
                        {capacity ? (
                            isFull ? (
                                <span
                                    data-testid="event-full-badge"
                                    className="px-2 py-0.5 rounded-full text-[10.5px] font-semibold bg-destructive/10 text-destructive border border-destructive/30"
                                >
                                    Event full
                                </span>
                            ) : almostFull ? (
                                <span
                                    data-testid="event-almost-full-badge"
                                    className="px-2 py-0.5 rounded-full text-[10.5px] font-semibold bg-amber-500/10 text-amber-700 dark:text-amber-300 border border-amber-500/30"
                                >
                                    Almost full
                                </span>
                            ) : null
                        ) : null}
                    </div>
                    {capacity ? (
                        <div className="mt-2.5 h-1.5 rounded-full bg-secondary overflow-hidden">
                            <div
                                data-testid="event-attendance-bar"
                                className="h-full rounded-full transition-[width] duration-500"
                                style={{
                                    width: `${pct}%`,
                                    background: isFull
                                        ? "hsl(var(--destructive))"
                                        : almostFull
                                        ? "linear-gradient(90deg, #f59e0b, #d97706)"
                                        : "linear-gradient(90deg, hsl(var(--primary)), hsl(var(--primary) / 0.65))",
                                }}
                            />
                        </div>
                    ) : null}
                </section>

                <section className="mt-5 p-4 rounded-2xl border border-border bg-card" data-testid="event-host-section">
                    <h3 className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground font-medium mb-2">
                        Host
                    </h3>
                    <div className="flex items-center gap-3">
                        {event.hostAvatar ? (
                            <img src={event.hostAvatar} alt="" className="h-12 w-12 rounded-full object-cover" />
                        ) : (
                            <InitialAvatar name={event.hostName || "Neyra"} size={48} />
                        )}
                        <div className="min-w-0 flex-1">
                            <div className="font-display text-[14px] font-semibold truncate flex items-center gap-1.5">
                                {event.hostName || "Neyra Partner"}
                                <Sparkles size={12} className="text-primary" />
                            </div>
                            {event.hostUsername && (
                                <div className="text-[12px] text-muted-foreground">@{event.hostUsername}</div>
                            )}
                        </div>
                    </div>
                </section>

                {/* Event chat access */}
                <section className="mt-5" data-testid="event-chat-section">
                    {joined ? (
                        <button
                            data-testid="event-open-chat-btn"
                            onClick={() => navigate(`/events/${event.id}/chat`)}
                            className="w-full inline-flex items-center gap-3 p-3.5 rounded-2xl border border-primary/30 bg-primary/5 text-left active:scale-[0.99] transition-transform"
                        >
                            <span className="h-10 w-10 rounded-xl bg-primary/15 text-primary flex items-center justify-center">
                                <MessageCircle size={18} />
                            </span>
                            <span className="flex-1 min-w-0">
                                <span className="block font-display text-[13.5px] font-semibold">
                                    Open Event Chat
                                </span>
                                <span className="block text-[11.5px] text-muted-foreground truncate">
                                    Chat with the host and other attendees
                                </span>
                            </span>
                        </button>
                    ) : (
                        <div
                            data-testid="event-chat-locked"
                            className="w-full flex items-center gap-3 p-3.5 rounded-2xl border border-dashed border-border bg-secondary/60 text-left"
                        >
                            <span className="h-10 w-10 rounded-xl bg-muted text-muted-foreground flex items-center justify-center">
                                <MessageCircle size={18} />
                            </span>
                            <span className="flex-1 min-w-0">
                                <span className="block font-display text-[13px] font-semibold">
                                    Event chat is locked
                                </span>
                                <span className="block text-[11.5px] text-muted-foreground">
                                    Join this event to access the event chat
                                </span>
                            </span>
                        </div>
                    )}
                </section>
            </div>

            {/* Bottom CTA */}
            <div className="absolute inset-x-0 bottom-0 px-5 pt-3 pb-[max(20px,env(safe-area-inset-bottom))] border-t border-border/60 bg-background">
                {cancelled ? (
                    canRefund ? (
                        <button
                            data-testid="event-refund-btn"
                            onClick={handleRequestRefund}
                            className="w-full h-14 rounded-2xl font-medium text-[15px] inline-flex items-center justify-center gap-2 text-primary-foreground active:scale-[0.98] transition-transform"
                            style={{
                                background:
                                    "linear-gradient(135deg, hsl(var(--primary)), hsl(var(--primary) / 0.78))",
                                boxShadow: "0 10px 28px -10px hsl(var(--primary) / 0.55)",
                            }}
                        >
                            <RotateCcw size={16} />
                            Request refund · {priceLabel}
                        </button>
                    ) : (
                        <div
                            data-testid="event-cancelled-state"
                            className="w-full h-14 rounded-2xl flex items-center justify-center gap-2 text-destructive font-medium text-[14px] bg-destructive/10 border border-destructive/30"
                        >
                            {alreadyRefunded ? "Refund issued to your wallet" : "Event cancelled"}
                        </div>
                    )
                ) : past ? (
                    <div
                        data-testid="event-past-state"
                        className="w-full h-14 rounded-2xl flex items-center justify-center gap-2 text-muted-foreground font-medium text-[14px] bg-muted border border-border"
                    >
                        <CalendarDays size={16} />
                        Past event — read only
                    </div>
                ) : isHost ? (
                    <button
                        data-testid="event-host-boost-btn"
                        onClick={() => navigate("/boost")}
                        className="w-full h-14 rounded-2xl font-medium text-[15px] inline-flex items-center justify-center gap-2 text-primary-foreground active:scale-[0.98] transition-transform"
                        style={{
                            background:
                                "linear-gradient(135deg, hsl(var(--primary)), hsl(var(--primary) / 0.78))",
                            boxShadow: "0 10px 28px -10px hsl(var(--primary) / 0.55)",
                        }}
                    >
                        <Rocket size={16} />
                        {user?.premium ? "Boost this event" : "Boost — Premium"}
                    </button>
                ) : joined ? (
                    <div
                        data-testid="event-joined-state"
                        className="w-full h-14 rounded-2xl flex items-center justify-center gap-2 text-primary font-medium text-[15px] bg-primary/10 border border-primary/25"
                    >
                        <CheckCircle2 size={18} />
                        {justJoined ? "You're in" : "You joined this event"}
                    </div>
                ) : isFull ? (
                    <button
                        data-testid="event-full-btn"
                        disabled
                        className="w-full h-14 rounded-2xl font-medium text-[15px] bg-muted text-muted-foreground border border-border cursor-not-allowed"
                    >
                        Event is full
                    </button>
                ) : (
                    <button
                        data-testid="event-join-btn"
                        onClick={handleJoin}
                        className="w-full h-14 rounded-2xl font-medium text-[15px] text-primary-foreground active:scale-[0.98] transition-transform"
                        style={{
                            background:
                                "linear-gradient(135deg, hsl(var(--primary)), hsl(var(--primary) / 0.78))",
                            boxShadow: "0 10px 28px -10px hsl(var(--primary) / 0.55)",
                        }}
                    >
                        {isFree ? "Join" : `Pay & join · ${priceLabel}`}
                    </button>
                )}
            </div>

            {/* Mock Paystack-style payment sheet (Card + Bank Transfer) */}
            {!isFree && (
                <PaymentSheet
                    open={showPay}
                    onClose={() => setShowPay(false)}
                    onSuccess={handlePaymentSuccess}
                    amount={Number(event.price) || 0}
                    title="Confirm event payment"
                    purposeLabel="event ticket"
                    summary={
                        <div className="flex items-center gap-3 p-3 rounded-2xl bg-muted/40 border border-border">
                            {event.image ? (
                                <img src={event.image} alt="" className="h-12 w-12 rounded-xl object-cover shrink-0" />
                            ) : (
                                <div className="h-12 w-12 rounded-xl overflow-hidden shrink-0">
                                    <InitialAvatar name={event.title} size={48} />
                                </div>
                            )}
                            <div className="min-w-0 flex-1">
                                <div className="font-display text-[14px] font-semibold truncate">
                                    {event.title}
                                </div>
                                <div className="text-[11.5px] text-muted-foreground truncate">
                                    {event.date}
                                </div>
                            </div>
                            <span
                                data-testid="event-pay-refund-type"
                                className={[
                                    "shrink-0 px-2 py-0.5 rounded-full text-[10.5px] font-semibold border",
                                    event.refundPolicy === "non_refundable"
                                        ? "bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/30"
                                        : "bg-primary/10 text-primary border-primary/30",
                                ].join(" ")}
                            >
                                {refundPolicyLabel}
                            </span>
                        </div>
                    }
                    refundNote={
                        <p className="text-center text-[11.5px] text-muted-foreground inline-flex items-center justify-center gap-1.5 w-full flex-wrap">
                            <ShieldCheck size={11} className="text-primary" />
                            <span>By joining this event, you agree to Neyra's</span>
                            <button
                                type="button"
                                data-testid="event-pay-refund-policy-link"
                                onClick={() => navigate("/refund-policy")}
                                className="font-semibold text-primary underline"
                            >
                                Refund Policy
                            </button>
                        </p>
                    }
                />
            )}

            {/* Refund success toast */}
            {refundDone && (
                <div
                    data-testid="event-refund-success"
                    role="status"
                    className="absolute left-1/2 -translate-x-1/2 top-[calc(env(safe-area-inset-top)+12px)] z-50 px-4 py-2.5 rounded-full bg-card border border-emerald-500/30 shadow-card text-emerald-600 dark:text-emerald-400 inline-flex items-center gap-2 text-[13px] font-medium animate-fade-up"
                >
                    <CheckCircle2 size={16} />
                    Refund credited to your wallet
                </div>
            )}

            <ReportSheet
                open={reportOpen}
                onClose={() => setReportOpen(false)}
                target={{ kind: "event", id: event.id, label: event.title }}
            />
        </div>
    );
};

export default EventDetail;
